import React from 'react';
import { Link } from 'react-router-dom';
import { Instagram, Mail } from 'lucide-react';
import { useLang } from '@/contexts/LanguageContext';
import { BRAND, WHATSAPP_LINK } from '@/data/brand';
import JellyLogo from '@/components/JellyLogo';

const TikTokIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.09a8.16 8.16 0 0 0 4.77 1.52V6.16a4.85 4.85 0 0 1-1.84-.47z"/>
  </svg>
);

const WhatsAppIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 2.1.55 4.07 1.6 5.84L2 22l4.4-1.15a9.84 9.84 0 0 0 5.64 1.78h.01c5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.9-7.02A9.86 9.86 0 0 0 12.04 2zm5.83 14.04c-.25.69-1.44 1.32-1.99 1.41-.51.08-1.15.11-1.86-.12-.43-.13-.99-.33-1.71-.64-3-1.3-4.96-4.32-5.11-4.52-.15-.2-1.22-1.62-1.22-3.1 0-1.47.77-2.2 1.05-2.5.27-.3.6-.37.8-.37.2 0 .4 0 .58.01.18.01.43-.07.67.51.25.6.86 2.07.94 2.22.08.15.13.33.03.53-.1.2-.15.32-.3.5-.15.18-.32.4-.45.54-.15.15-.3.32-.13.62.18.3.78 1.28 1.67 2.07 1.15 1.02 2.12 1.34 2.42 1.49.3.15.48.13.65-.08.18-.2.75-.87.95-1.17.2-.3.4-.25.67-.15.27.1 1.72.81 2.02.96.3.15.5.22.57.35.07.13.07.74-.18 1.45z"/>
  </svg>
);

export default function Footer() {
  const { t } = useLang();
  return (
    <footer className="bg-[#1F1A24] text-white pt-20 pb-10 px-6 md:px-12 lg:px-24 relative overflow-hidden">
      <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-[#FF7597]/20 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-32 -left-32 w-96 h-96 rounded-full bg-[#C3A6DF]/20 blur-3xl pointer-events-none" />

      <div className="relative max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-10">
        <div className="col-span-2 md:col-span-2">
          <JellyLogo size="lg" color="#FF2D9C" bylineColor="#FF2D9C" />
          <h3 className="mt-6 font-display text-3xl md:text-4xl font-bold tracking-tighter max-w-md text-balance">
            {t('footer.tagline')}
          </h3>
          <p className="mt-4 text-white/60 max-w-sm text-sm">
            {t('footer.backed')}
          </p>

          <div className="mt-6 flex gap-3">
            <a
              data-testid="footer-instagram"
              href={`https://instagram.com/${BRAND.instagram}`}
              target="_blank"
              rel="noreferrer"
              className="h-10 w-10 rounded-full bg-white/10 hover:bg-[#FF7597] inline-flex items-center justify-center transition-colors"
              aria-label="Instagram"
            >
              <Instagram className="w-4 h-4" />
            </a>
            <a
              data-testid="footer-tiktok"
              href={`https://tiktok.com/@${BRAND.tiktok}`}
              target="_blank"
              rel="noreferrer"
              className="h-10 w-10 rounded-full bg-white/10 hover:bg-[#FF7597] inline-flex items-center justify-center transition-colors"
              aria-label="TikTok"
            >
              <TikTokIcon className="w-4 h-4" />
            </a>
            <a
              data-testid="footer-whatsapp"
              href={WHATSAPP_LINK('Hola Jelly!')}
              target="_blank"
              rel="noreferrer"
              className="h-10 w-10 rounded-full bg-white/10 hover:bg-[#25D366] inline-flex items-center justify-center transition-colors"
              aria-label="WhatsApp"
            >
              <WhatsAppIcon className="w-4 h-4" />
            </a>
            <a
              data-testid="footer-email"
              href={`mailto:${BRAND.email}`}
              className="h-10 w-10 rounded-full bg-white/10 hover:bg-[#FF7597] inline-flex items-center justify-center transition-colors"
              aria-label="Email"
            >
              <Mail className="w-4 h-4" />
            </a>
          </div>
        </div>

        <div>
          <p className="text-[10px] tracking-[0.25em] font-bold text-[#FF7597] mb-4">{t('footer.shop')}</p>
          <ul className="space-y-2 text-sm text-white/80">
            <li><Link to="/productos" className="hover:text-[#FF7597]">{t('nav.products')}</Link></li>
            <li><Link to="/distribuidores" className="hover:text-[#FF7597]">{t('nav.distributors')}</Link></li>
          </ul>
        </div>
        <div>
          <p className="text-[10px] tracking-[0.25em] font-bold text-[#FF7597] mb-4">{t('footer.brand')}</p>
          <ul className="space-y-2 text-sm text-white/80">
            <li><Link to="/sobre" className="hover:text-[#FF7597]">{t('nav.about')}</Link></li>
            <li><Link to="/contacto" className="hover:text-[#FF7597]">{t('nav.contact')}</Link></li>
          </ul>
        </div>
      </div>

      <div className="relative max-w-7xl mx-auto mt-16 pt-6 border-t border-white/10 flex flex-col md:flex-row items-start md:items-center justify-between gap-2 text-xs text-white/50">
        <span>© {new Date().getFullYear()} Jelly by Enkor. {t('footer.rights')}</span>
        <div className="flex items-center gap-4">
          <Link
            to="/privacidad"
            data-testid="footer-privacy"
            className="text-white/60 hover:text-[#FF7597] transition-colors tracking-widest uppercase text-[10px]"
          >
            Privacidad
          </Link>
          <Link
            to="/admin"
            data-testid="footer-admin"
            className="text-white/40 hover:text-[#FF7597] transition-colors tracking-widest uppercase text-[10px]"
          >
            Admin
          </Link>
          <span>{BRAND.email}</span>
        </div>
      </div>
    </footer>
  );
}
